/**
 * conversationStatusVocabulary — the one shared vocabulary for a Conversation's `status`, the
 * human-facing state of the product's core object and the field every conversation surface names.
 *
 * The scale is closed and declared once. `app/models/conversation.py:30-38` fixes
 * `CONVERSATION_STATUSES` as a seven-tuple — open, awaiting_agent, awaiting_approval,
 * awaiting_human, handed_off, resolved, closed — under a comment that states the rule this field
 * lives by: it is the *human-facing* status only and is never a substitute for `AgentRun.status`
 * on any execution the conversation triggers. Every write is validated against that tuple, and
 * the backend contract test pins a label map's key set to it by equality.
 *
 * The wording, unlike the scale, was declared eleven times and disagreed with itself.
 *
 *   - The backend worded it twice, on the SAME stored value: `routers/conversations.py:65` said
 *     `awaiting_human` -> "ממתינה לאדם" and `resolved` -> "טופלה" while
 *     `services/conversation_center.py:78` said "ממתינה לטכנאי" and "נפתרה". Two endpoints of one
 *     API therefore described one conversation two ways; the second is the one an operator reads,
 *     because the conversations workspace and the thread header render the served `status_label`
 *     (`ConversationsPage.jsx:179`, `ConversationDetailPage.jsx:41`). Both copies now carry this
 *     module's wording and this module's test pins them equal to each other value for value.
 *   - The frontend worded it eight more times, in eight page-local maps, five of which said
 *     `awaiting_agent` -> "ממתינה לסוכן" against the served "ממתינה לעובד" — and two of those five
 *     carry a comment claiming to mirror "the conversations screen" (TicketDetail's
 *     `EMPLOYEE_ACTIVITY_CONVERSATION_STATUS`, PilotAcceptancePage's `CONVERSATION_STATUS_HE`),
 *     which is exactly the claim the served label falsifies. Those maps are deleted.
 *   - One of the eight was short of the scale. `SystemMapLivePage.jsx:70` held four arms (open,
 *     closed, awaiting_human, awaiting_approval) and fell through to the raw value otherwise, so
 *     `awaiting_agent`, `handed_off` and `resolved` reached the Live Operations Map's conversation
 *     stage as the producer's own English identifiers, off `GET /api/conversations/` — the one
 *     reachable raw print in this family, and the reason this iteration exists. Six of the other
 *     seven fell through to the raw value for anything off-scale too, but all seven arms were on
 *     the scale, so those fall-throughs were latent rather than live.
 *
 * The wording here is the one the API already served on the product's conversations screen, which
 * is also the wording two of the eight copies already used: `awaiting_agent` -> "ממתינה לעובד"
 * (the digital-employee term every other capability-3/5 surface uses), `awaiting_human` ->
 * "ממתינה לטכנאי" (the pilot's human IS a technician), `resolved` -> "נפתרה". Nothing else about
 * any arm changes: four of the seven arms were already worded identically by every copy.
 */

export const CONVERSATION_STATUS_HE = {
  open: "פתוחה",
  awaiting_agent: "ממתינה לעובד",
  awaiting_approval: "ממתינה לאישור",
  awaiting_human: "ממתינה לטכנאי",
  handed_off: "הועברה",
  resolved: "נפתרה",
  closed: "סגורה",
};

/**
 * A `status` this build does not name. Named rather than echoed: every value this field can hold
 * is one of seven English identifiers, so a token this map does not hold is a producer change or a
 * row written before the validation existed, and printing it would put an English word inside a
 * Hebrew line — the defect this module exists to remove. The cost is stated rather than hidden: a
 * producer rename reads as this arm instead of as the new token, which is why the parity half of
 * this module's test re-derives the scale from the Python as text.
 *
 * A MISSING value reads as this arm too. That is deliberate and it is the one place this module
 * changes a behaviour rather than a wording: `workEvidenceModel` used to draw no chip at all for a
 * record with no `status`, and every value this field can legally hold is on the scale, so a
 * record without one is a fact the reader should be told rather than a fact to hide.
 */
export const CONVERSATION_STATUS_UNKNOWN_HE = "מצב השיחה לא זוהה בגרסה זו";

/** The scale's own order (`CONVERSATION_STATUSES`), for a list that must not sort alphabetically. */
export const CONVERSATION_STATUS_ORDER = [
  "open",
  "awaiting_agent",
  "awaiting_approval",
  "awaiting_human",
  "handed_off",
  "resolved",
  "closed",
];

/**
 * The badge palette for the same seven arms. Lifted out of `conversationPresentation.STATUS_BADGE`,
 * which owned it for the two conversation screens while three other files kept their own copy.
 * `awaiting_human` shares amber with `awaiting_approval` because "waiting on a person" and "waiting
 * on a human decision" are the two states an operator most needs to tell apart from "the employee
 * will continue" — the violet of `awaiting_agent`.
 *
 * This is NOT the palette `EmployeeWorkspace.CONVERSATION_STATE_STYLE` draws, which gives
 * `awaiting_human` and `handed_off` their own hues for a stated reason (its own list sits beside a
 * second, employee-side register and it needs the two amber states separated). That list keeps its
 * palette, its glyphs and its titles and takes only its label from here.
 */
export const CONVERSATION_STATUS_BADGE = {
  open: "bg-blue-100 text-blue-700",
  awaiting_agent: "bg-violet-100 text-violet-700",
  awaiting_approval: "bg-amber-100 text-amber-700",
  awaiting_human: "bg-amber-100 text-amber-700",
  handed_off: "bg-gray-200 text-gray-600",
  resolved: "bg-green-100 text-green-700",
  closed: "bg-gray-200 text-gray-500",
};

const has = (map, value) => Object.prototype.hasOwnProperty.call(map, value);

/**
 * The Hebrew wording for a conversation `status`.
 *
 * Resolved with `hasOwnProperty` rather than a bare lookup: a bare `CONVERSATION_STATUS_HE[value]`
 * answers an inherited key (`constructor`, `toString`) with a function, which a `|| fallback`
 * treats as a hit and renders.
 */
export function conversationStatusLabel(value) {
  return has(CONVERSATION_STATUS_HE, value) ? CONVERSATION_STATUS_HE[value] : CONVERSATION_STATUS_UNKNOWN_HE;
}

export function conversationStatusBadgeClass(value) {
  return has(CONVERSATION_STATUS_BADGE, value) ? CONVERSATION_STATUS_BADGE[value] : "bg-gray-100 text-gray-600";
}

/**
 * `{ label, badge }` for an arm this build names, and `null` for one it does not.
 *
 * The null is load-bearing for the one caller that gates on this: `TicketDetail`'s employee-activity
 * row draws a status chip only when there is a status it can name. That is a filter rather than an
 * echo — nothing reaches the DOM off it — so this iteration leaves the gate exactly as it was and
 * gives it a shared arm-set to filter against, instead of widening the row with a chip for a state
 * this build cannot describe.
 */
export function conversationStatusMeta(value) {
  return has(CONVERSATION_STATUS_HE, value)
    ? { label: CONVERSATION_STATUS_HE[value], badge: CONVERSATION_STATUS_BADGE[value] }
    : null;
}
